import {
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileSpreadsheet,
  FileCode,
  File as FileIcon,
  type LucideIcon,
} from 'lucide-react'
import type { FilesFolder, FilesObject } from '../../services/api'

// --- View mode -------------------------------------------------------------

export type FilesViewMode = 'list' | 'grid'

export const VIEW_MODE_STORAGE_KEY = 'files:viewMode'

export function loadViewMode(): FilesViewMode {
  try {
    const v = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    if (v === 'grid' || v === 'list') return v
  } catch {}
  return 'list'
}

export function saveViewMode(mode: FilesViewMode) {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  } catch {}
}

// --- Sorting ---------------------------------------------------------------

export type FilesSortKey =
  | 'name-asc'
  | 'name-desc'
  | 'date-desc'
  | 'date-asc'
  | 'size-desc'
  | 'size-asc'
  | 'type'

export const SORT_STORAGE_KEY = 'files:sort'

export const DEFAULT_SORT: FilesSortKey = 'date-desc'

export const SORT_OPTIONS: { key: FilesSortKey; label: string }[] = [
  { key: 'date-desc', label: 'Newest first' },
  { key: 'date-asc', label: 'Oldest first' },
  { key: 'name-asc', label: 'Name (A–Z)' },
  { key: 'name-desc', label: 'Name (Z–A)' },
  { key: 'size-desc', label: 'Size (largest)' },
  { key: 'size-asc', label: 'Size (smallest)' },
  { key: 'type', label: 'Type' },
]

export function loadSortKey(): FilesSortKey {
  try {
    const v = localStorage.getItem(SORT_STORAGE_KEY)
    if (v && SORT_OPTIONS.some((o) => o.key === v)) return v as FilesSortKey
  } catch {}
  return DEFAULT_SORT
}

export function saveSortKey(key: FilesSortKey) {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, key)
  } catch {}
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
}

/** Folders have no size/date surfaced; sort by name, honouring name direction. */
export function sortFolders(folders: FilesFolder[], key: FilesSortKey): FilesFolder[] {
  const dir = key === 'name-desc' ? -1 : 1
  return [...folders].sort((a, b) => dir * collator.compare(a.name, b.name))
}

export function sortObjects(objects: FilesObject[], key: FilesSortKey): FilesObject[] {
  const arr = [...objects]
  arr.sort((a, b) => {
    switch (key) {
      case 'name-asc':
        return collator.compare(a.name, b.name)
      case 'name-desc':
        return collator.compare(b.name, a.name)
      case 'date-asc':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      case 'date-desc':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      case 'size-asc':
        return a.size - b.size || collator.compare(a.name, b.name)
      case 'size-desc':
        return b.size - a.size || collator.compare(a.name, b.name)
      case 'type': {
        const ta = extensionOf(a.name) || a.mimeType
        const tb = extensionOf(b.name) || b.mimeType
        return collator.compare(ta, tb) || collator.compare(a.name, b.name)
      }
      default:
        return 0
    }
  })
  return arr
}

// --- File-type helpers -----------------------------------------------------

export function isImage(obj: FilesObject): boolean {
  return obj.mimeType.startsWith('image/')
}

const CODE_EXT = new Set([
  'js', 'ts', 'jsx', 'tsx', 'json', 'html', 'css', 'py', 'rb', 'go', 'rs', 'java',
  'c', 'cpp', 'sh', 'yml', 'yaml', 'xml', 'sql',
])
const ARCHIVE_EXT = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'bz2'])
const SHEET_EXT = new Set(['xls', 'xlsx', 'csv', 'tsv', 'numbers'])

export function fileIcon(obj: FilesObject): LucideIcon {
  const mime = obj.mimeType
  if (mime.startsWith('image/')) return FileImage
  if (mime.startsWith('video/')) return FileVideo
  if (mime.startsWith('audio/')) return FileAudio
  const ext = extensionOf(obj.name)
  if (ARCHIVE_EXT.has(ext)) return FileArchive
  if (SHEET_EXT.has(ext)) return FileSpreadsheet
  if (CODE_EXT.has(ext)) return FileCode
  if (mime === 'application/pdf' || mime.startsWith('text/')) return FileText
  return FileIcon
}
